import { useCallback, useState } from 'react';
import { endpoints } from '../api/client.js';
import { useResource } from '../hooks/useResource.js';
import { Panel } from './Panel.jsx';
import { ErrorState, LoadingState } from './StateViews.jsx';
import { formatClock } from '../lib/format.js';

// API.md clamps `lines` to 1–2000; every option here is inside that range.
const LINE_OPTIONS = [50, 100, 250, 500, 1000, 2000];

export default function LogsPanel({ siteName }) {
  const [lines, setLines] = useState(100);
  const [slot, setSlot] = useState('preview');

  const load = useCallback(
    (signal) => endpoints.siteLogs(siteName, slot, lines, signal),
    [siteName, slot, lines],
  );
  const state = useResource(load, [siteName, slot, lines]);

  const logLines = state.data?.lines;

  return (
    <Panel
      title="Logs"
      subtitle={`Most recent ${slot} journal output for ${siteName}`}
      id="logs"
      actions={
        <>
          <label className="select" htmlFor="log-slot">
            <span className="select__label">Slot</span>
            <select
              id="log-slot"
              className="select__input mono"
              value={slot}
              onChange={(event) => setSlot(event.target.value)}
            >
              <option value="preview">preview</option>
              <option value="live">live</option>
            </select>
          </label>
          <label className="select" htmlFor="log-lines">
            <span className="select__label">Lines</span>
            <select
              id="log-lines"
              className="select__input mono"
              value={lines}
              onChange={(event) => setLines(Number(event.target.value))}
            >
              {LINE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn" onClick={state.refresh} disabled={state.status === 'loading'}>
            {state.status === 'loading' ? 'Refreshing…' : 'Refresh'}
          </button>
        </>
      }
      footer={
        state.updatedAt ? (
          <span className="panel__foot-note">
            Fetched <span className="mono">{formatClock(state.updatedAt)}</span>
            {Array.isArray(logLines) ? (
              <>
                {' '}
                · <span className="mono">{logLines.length}</span> line
                {logLines.length === 1 ? '' : 's'} returned
              </>
            ) : null}
          </span>
        ) : null
      }
    >
      {state.status === 'loading' && !logLines ? (
        <LoadingState label="Reading the journal…" rows={4} />
      ) : state.status === 'error' ? (
        <ErrorState error={state.error} onRetry={state.refresh} title="Could not read logs" />
      ) : !Array.isArray(logLines) || logLines.length === 0 ? (
        <p className="state__body">
          No log lines were returned. The unit may not have started yet, or the journal may have
          been rotated.
        </p>
      ) : (
        <pre className="logs mono" tabIndex={0} aria-label={`Last ${logLines.length} log lines`}>
          {logLines.map((line, index) => (
            <span className="logs__line" key={`${index}-${line}`}>
              <span className="logs__gutter" aria-hidden="true">
                {index + 1}
              </span>
              <span className="logs__text">{line}</span>
            </span>
          ))}
        </pre>
      )}
    </Panel>
  );
}
